import { AdminPermissions } from '@/features/auth/auth.type';
import {
  ProvisioningStatus,
  ServiceTierExtended,
} from '@/features/provisioning/provisioning.type';
import { localStorageKeys } from '@/utils/local-storage';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AuthStore = {
  accessToken: string | undefined;
  setAccessToken: (accessToken: string | undefined) => void;

  accessTokenExpiresAt: number | undefined;
  setAccessTokenExpiresAt: (
    accessTokenExpiresAt: number | undefined,
  ) => void;

  adminPermissions: AdminPermissions;
  setAdminPermissions: (adminPermissions: AdminPermissions) => void;

  logoUrl: string | undefined;
  setLogoUrl: (logoUrl: string | undefined) => void;

  organizationId: string | undefined;
  setOrganizationId: (organizationId: string | undefined) => void;

  refreshToken: string | undefined;
  setRefreshToken: (refreshToken: string | undefined) => void;

  serviceTier: ServiceTierExtended;
  setServiceTier: (serviceTier: ServiceTierExtended) => void;

  serviceTierStatus: ProvisioningStatus;
  setServiceTierStatus: (
    serviceTierStatus: ProvisioningStatus,
  ) => void;

  tokenLimit: number;
  setTokenLimit: (tokenLimit: number) => void;

  tokenLimitPeriod: 'daily' | 'monthly';
  setTokenLimitPeriod: (
    tokenLimitPeriod: 'daily' | 'monthly',
  ) => void;

  userEmail: string | undefined;
  setUserEmail: (userEmail: string | undefined) => void;

  userId: string | undefined;
  setUserId: (userId: string | undefined) => void;
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      accessToken: undefined,
      setAccessToken: (accessToken: string | undefined) =>
        set({ accessToken }),

      accessTokenExpiresAt: undefined,
      setAccessTokenExpiresAt: (
        accessTokenExpiresAt: number | undefined,
      ) => set({ accessTokenExpiresAt }),

      adminPermissions: {
        application: false,
        index: false,
        model: false,
        sub: false,
        user: false,
      },
      setAdminPermissions: (adminPermissions: AdminPermissions) =>
        set({ adminPermissions }),

      logoUrl: undefined,
      setLogoUrl: (logoUrl: string | undefined) => set({ logoUrl }),

      organizationId: undefined,
      setOrganizationId: (organizationId: string | undefined) =>
        set({ organizationId }),

      refreshToken: undefined,
      setRefreshToken: (refreshToken: string | undefined) =>
        set({ refreshToken }),

      serviceTier: 'SINGLE',
      setServiceTier: (serviceTier: ServiceTierExtended) =>
        set({ serviceTier }),

      serviceTierStatus: 'READY',
      setServiceTierStatus: (serviceTierStatus: ProvisioningStatus) =>
        set({ serviceTierStatus }),

      tokenLimit: 0,
      setTokenLimit: (tokenLimit: number) => set({ tokenLimit }),

      tokenLimitPeriod: 'monthly',
      setTokenLimitPeriod: (tokenLimitPeriod: 'daily' | 'monthly') =>
        set({ tokenLimitPeriod }),

      userEmail: undefined,
      setUserEmail: (userEmail: string | undefined) =>
        set({ userEmail }),

      userId: undefined,
      setUserId: (userId: string | undefined) => set({ userId }),
    }),
    {
      name: localStorageKeys.auth,
    },
  ),
);
